import type { MutableState } from 'src/replicant/State';
import { CareItemID } from '../care/care.ruleset';
import * as Inventory from './';
import {
  CurrencyConsumeOpts,
  CurrencyGrantOpts,
  CurrencyReplicantAPI,
  consumeCurrency,
  grantCurrency,
} from './inventory.utils';
import {
  CurrencyConsumeFeature,
  CurrencyGrantFeature,
} from '../analytics/events';

/** Set initial inventory. */
export function setInitialInventory(state: MutableState): void {
  if (state.lastSessionAt) return;
}

/** Add points */
export function addPoints(
  state: MutableState,
  amount: number,
  feature: CurrencyGrantFeature,
  api: CurrencyReplicantAPI,
  opts?: CurrencyGrantOpts,
): void {
  if (amount < 0) throw new Error('Negative addPoints amount');
  const modifier = () => {
    state.inventory.points += amount;
  };
  if (feature && api) grantCurrency(state, modifier, feature, api, opts);
  else modifier();
}

/** Consume points */
export function consumePoints(
  state: MutableState,
  amount: number,
  feature: CurrencyConsumeFeature,
  api: CurrencyReplicantAPI,
  opts?: CurrencyConsumeOpts,
): void {
  if (amount < 0) throw new Error('Negative consumePoints amount');
  const { inventory } = state;
  if (inventory.points < amount)
    throw new Error('Not enough points to consume');
  const modifier = () => {
    inventory.points -= amount;
  };
  if (feature && api) consumeCurrency(state, modifier, feature, api, opts);
  else modifier();
}

/** Set points */
export function setPoints(state: MutableState, amount: number): void {
  if (amount < 0) throw new Error('Negative setPoints amount');
  state.inventory.points = amount;
}

/** Add free gems */
export function addFreeGems(
  state: MutableState,
  amount: number,
  feature: CurrencyGrantFeature,
  api: CurrencyReplicantAPI,
  opts?: CurrencyGrantOpts,
): void {
  if (amount < 0) throw new Error('Negative addFreeGems amount');
  const modifier = () => {
    state.inventory.premium.free += amount;
  };
  if (feature && api) grantCurrency(state, modifier, feature, api, opts);
  else modifier();
}

/** Set free gems */
export function setFreeGems(state: MutableState, amount: number): void {
  if (amount < 0) throw new Error('Negative setFreeGems amount');
  state.inventory.premium.free = amount;
}
/** Add paid gems */
export function addPaidGems(
  state: MutableState,
  amount: number,
  feature: CurrencyGrantFeature,
  api: CurrencyReplicantAPI,
  opts?: CurrencyGrantOpts,
): void {
  if (amount < 0) throw new Error('Negative addPaidGems amount');
  const modifier = () => {
    state.inventory.premium.paid += amount;
  };
  if (feature && api) grantCurrency(state, modifier, feature, api, opts);
  else modifier();
}

/** Consume gems, paid gems first */
export function consumeGems(
  state: MutableState,
  amount: number,
  feature: CurrencyConsumeFeature,
  api: CurrencyReplicantAPI,
  opts?: CurrencyConsumeOpts,
): void {
  if (amount < 0) throw new Error('Negative amount amount');
  const { inventory } = state;
  const sum = inventory.premium.paid + inventory.premium.free;
  if (sum < amount) throw new Error('Not enough gems to consume');
  const modifier = () => {
    const paid = Math.min(inventory.premium.paid, amount);
    const free = Math.max(amount - paid, 0);
    inventory.premium.paid -= paid;
    inventory.premium.free -= free;
  };
  if (feature && api) consumeCurrency(state, modifier, feature, api, opts);
  else modifier();
}

/** Add energy */
export function addEnergy(
  state: MutableState,
  amount: number,
  now: number,
  feature: CurrencyGrantFeature,
  api: CurrencyReplicantAPI,
  opts?: CurrencyGrantOpts,
): void {
  if (amount === undefined || amount === null)
    throw new Error('addEnergy: Can not be undefined or null');
  if (isNaN(amount)) throw new Error('addEnergy: Can not be NaN');
  if (amount < 0) throw new Error('addEnergy: Amount cannot be less then 0');
  const modifier = () => {
    const previousEnergy = Inventory.getters.getEnergy(state, now);
    const { inventory } = state;
    inventory.energy = previousEnergy + amount;
    const duration =
      (now - state.inventory.energyRechargeStartTime) %
      Inventory.getters.getEnergyRegenerationConfig(state).time;
    inventory.energyRechargeStartTime = now - duration;
  };
  if (feature && api) grantCurrency(state, modifier, feature, api, opts);
  else modifier();
}

/** Set energy */
export function setEnergy(
  state: MutableState,
  amount: number,
  now: number,
): void {
  if (amount < 0) throw new Error('setEnergy: Amount cannot be less then 0');
  const { inventory } = state;
  inventory.energy = amount;
  const duration =
    (now - state.inventory.energyRechargeStartTime) %
    Inventory.getters.getEnergyRegenerationConfig(state).time;
  inventory.energyRechargeStartTime = now - duration;
}

/** Consume energy */
export function consumeEnergy(
  state: MutableState,
  amount: number,
  now: number,
  feature: CurrencyConsumeFeature,
  api: CurrencyReplicantAPI,
  opts?: CurrencyConsumeOpts,
): void {
  if (amount < 0)
    throw new Error('consumeEnergy: Amount cannot be less then 0');
  const previousEnergy = Inventory.getters.getEnergy(state, now);
  if (amount > previousEnergy)
    throw new Error('consumeEnergy: energy not enough');
  const modifier = () => {
    const { inventory } = state;
    inventory.energy = Math.max(0, previousEnergy - amount);
    const maxEnergy = Inventory.getters.getMaxEnergy(state);
    const wasFull = previousEnergy >= maxEnergy;
    const isFull = inventory.energy >= maxEnergy;
    if (wasFull && !isFull) {
      inventory.energyRechargeStartTime = now;
      return;
    }
    const timeToNextEnergy =
      (now - inventory.energyRechargeStartTime) %
      Inventory.getters.getEnergyRegenerationConfig(state).time;
    inventory.energyRechargeStartTime = now - timeToNextEnergy;
  };
  if (feature && api) consumeCurrency(state, modifier, feature, api, opts);
  else modifier();
}

/** Restore energy to max */
export function resetEnergy(state: MutableState, now: number): void {
  const energy = Inventory.getters.getEnergyToFull(state, now);
  const { inventory } = state;
  if (energy) {
    inventory.energy = Inventory.getters.getMaxEnergy(state);
    inventory.energyRechargeStartTime = now;
  }
}
/** Consume care items. */
export function consumeCareItems(
  state: MutableState,
  id: CareItemID,
  amount: number,
): void {
  if (amount < 0)
    throw new Error('Cannot consume negative amount of care items');
  const { inventory } = state;
  const sum = inventory.care[id]?.count ?? 0;
  if (sum < amount) throw new Error('Not enough care items to consume');
  inventory.care[id].count -= amount;
  if (inventory.care[id].count === 0) delete inventory.care[id];
}

/** Add stars. */
export function addStars(state: MutableState, amount: number): void {
  if (amount < 0) throw new Error('Cannot add negative amount of stars');
  state.inventory.stars += amount;
}

/** Consume stars. */
export function consumeStars(state: MutableState, amount: number): void {
  if (amount < 0) throw new Error('Cannot consume negative amount of stars');
  const { inventory } = state;
  if (inventory.stars < amount) throw new Error('Not enough stars to consume');
  inventory.stars -= amount;
}
